import React, { useState } from "react";
import VuiBox from "components/VuiBox";
import VuiAvatar from "components/VuiAvatar";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import PropTypes from "prop-types";
import { useTranslation } from 'react-i18next';
import VuiBadge from "components/VuiBadge";
import { Link } from "react-router-dom";
import ShareButton from 'components/CustomButtons/ShareButton';
import dataService from 'api/data.service';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import Divider from "@mui/material/Divider";

function UpcomingEventCard({ image, label, title, description, actions = [], options = [], visible, event_type, games, event_id, event_date, players, owner, owner_id, event_bookmarked, languages }) {
  const { t } = useTranslation();
  const [bookmarked, setBookmarked] = useState(event_bookmarked);

  const handleBookmark = async () => {
    try {
      const action = bookmarked ? 'unbookmark' : 'bookmark';
      await dataService.bookmarkEvent(event_id, action);
      setBookmarked(!bookmarked);
    } catch (error) {
      console.error(error.message);
    }
  };

  const storedData = localStorage.getItem('currentUser');
  let currentData = JSON.parse(storedData);
  if (currentData && currentData.exp && currentData.exp * 1000 < Date.now()) {
    localStorage.removeItem('currentUser');
    currentData = null;
  }

  const squareSize = 300;
  const thumbnailSize = squareSize / Math.ceil(Math.sqrt(games.length));
  const renderGameThumbnails = games.map((game, index) => (
    <Link key={index} to={`/game/${game.id}`} style={{ textDecoration: "none" }} title={game.title}>
      <VuiAvatar
        src={game.thumbnail}
        alt={game.name}
        title={game.name}
        sx={{
          width: `${thumbnailSize}px`,
          height: `${thumbnailSize}px`,
          borderRadius: "8px",
          objectFit: "cover",
        }}
      />
    </Link>
  ));

  const formatDate = (date) => {
    const eventDate = new Date(date);
    const year = eventDate.getFullYear();
    const month = String(eventDate.getMonth() + 1).padStart(2, '0');
    const day = String(eventDate.getDate()).padStart(2, '0');
    const hours = String(eventDate.getHours()).padStart(2, '0');
    const minutes = String(eventDate.getMinutes()).padStart(2, '0');
    return `${year}/${month}/${day} ${hours}:${minutes}`;
  };

  const EventTypeTag = ({ eventType }) => {
    const getTagColor = (eventType) => {
      switch (eventType) {
        case 'public': return 'info';
        case 'private': return 'warning';
        default: return 'text';
      }
    };
    return (
      <VuiBadge
        badgeContent={eventType === 'public' ? t('openEvent') : t('privateEvent')}
        size="xs"
        color={getTagColor(eventType)}
        container
        ml={1}
      />
    );
  };

  const VisibilityTag = ({ visible }) => (
    <VuiBadge
      badgeContent={visible ? t('visibleEvent') : t('invisibleEvent')}
      size="xs"
      color={visible ? 'success' : 'error'}
      container
      ml={1}
    />
  );

  const LanguagesTag = ({ languages }) => (
    <>
      {languages.map((language, index) => (
        <VuiBadge
          key={index}
          badgeContent={t(`languages.${language}`)}
          size="xs"
          color="primary"
          container
          sx={{ marginRight: '4px' }}
        />
      ))}
    </>
  );

  const renderOptions = options.map(({ image: media, name }) => (
    <VuiAvatar
      key={name}
      src={media}
      alt={name}
      size="xs"
      sx={({ borders: { borderWidth }, palette: { dark }, functions: { rgba } }) => ({
        border: `${borderWidth[2]} solid ${rgba(dark.focus, 0.5)}`,
        cursor: "pointer",
        position: "relative",
        ml: -1.25,
        "&:hover, &:focus": { zIndex: "10" },
      })}
    />
  ));

  return (
    <VuiBox>
      <VuiBox
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          boxShadow: "none",
          overflow: "visible",
          mb: 2,
          gap: { xs: 2, md: 0 },
        }}
      >
        {/* Text content */}
        <VuiBox
          sx={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            minWidth: 0,
          }}
        >
          <VuiTypography variant="h6" color="text" fontWeight="medium">
            {formatDate(event_date)}
          </VuiTypography>
          <VuiTypography variant="h3" color="text" fontWeight="medium" sx={{ wordBreak: 'break-word' }}>
            {title}
          </VuiTypography>
          <VuiBox mb={1} sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            <EventTypeTag eventType={event_type} />
            <VisibilityTag visible={visible} />
            <LanguagesTag languages={languages} />
          </VuiBox>
          <VuiBox mb={1}>
            <VuiTypography variant="h5" color="white">
              {label}
            </VuiTypography>
          </VuiBox>
          <VuiBox mb={3} lineHeight={0} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
            <VuiTypography
              variant="h6"
              fontWeight="regular"
              color="text"
              dangerouslySetInnerHTML={{ __html: description }}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
            />
          </VuiBox>
          <VuiBox mb={2}>
            <VuiTypography variant="h6" color="text" fontWeight="medium">
              {t('willAttend', { count: players })} · {t('organizer')}{' '}
              <Link to={`/user/${owner_id}`} style={{ textDecoration: 'none', color: 'white' }}>{owner}</Link>
            </VuiTypography>
          </VuiBox>
          <VuiBox
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            sx={{ gap: 1 }}
          >
            <VuiBox display="flex" flexWrap="wrap" sx={{ gap: 1 }}>
              {actions.map((action, index) => (
                <VuiButton
                  key={index}
                  component={action.type === "internal" ? Link : "a"}
                  to={action.type === "internal" ? action.route : null}
                  href={action.type === "external" ? action.route : null}
                  target={action.type === "external" ? "_blank" : null}
                  rel={action.type === "external" ? "noreferrer" : null}
                  variant="outlined"
                  size="small"
                  color={action.color}
                  onClick={action.onClick}
                >
                  {action.label}
                  {action.icon}
                </VuiButton>
              ))}
              {currentData && currentData.user_id && (
                <VuiButton
                  size="small"
                  variant="contained"
                  color={bookmarked ? "warning" : "light"}
                  onClick={handleBookmark}
                  startIcon={bookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                >
                  {bookmarked ? t('unbookmark') : t('bookmark')}
                </VuiButton>
              )}
              <VuiBox>
                <ShareButton
                  title={title}
                  text={t('lookAtThisEvent')}
                  url={`${process.env.REACT_APP_DEFAULT_DOMAIN}/event/${event_id}`}
                />
              </VuiBox>
            </VuiBox>
            <VuiBox display="flex">{renderOptions}</VuiBox>
          </VuiBox>
        </VuiBox>

        {/* Image / thumbnails */}
        <VuiBox
          sx={{
            flexShrink: 0,
            width: { xs: '100%', md: '250px' },
            mt: { xs: 0, md: 0 },
            ml: { md: 2 },
            display: "flex",
            alignItems: "flex-start",
            justifyContent: { xs: "center", md: "flex-start" },
          }}
        >
          {image && image.length > 0 ? (
            <VuiBox
              component="img"
              src={image}
              borderRadius="15px"
              sx={{ width: "100%", height: "auto", maxWidth: { xs: '100%', md: '250px' } }}
            />
          ) : (
            <VuiBox
              sx={{
                width: { xs: '100%', md: `${squareSize}px` },
                maxWidth: `${squareSize}px`,
                aspectRatio: '1 / 1',
                display: "flex",
                flexWrap: "wrap",
              }}
            >
              {renderGameThumbnails}
            </VuiBox>
          )}
        </VuiBox>
      </VuiBox>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
    </VuiBox>
  );
}

UpcomingEventCard.propTypes = {
  image: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(["external", "internal"]),
      route: PropTypes.string.isRequired,
      color: PropTypes.oneOf(["white", "text", "info", "success", "warning", "error", "light", "dark"]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  event_type: PropTypes.string.isRequired,
  visible: PropTypes.bool,
  games: PropTypes.arrayOf(PropTypes.object).isRequired,
  players: PropTypes.number.isRequired,
  owner: PropTypes.string.isRequired,
  owner_id: PropTypes.string.isRequired,
  languages: PropTypes.arrayOf(PropTypes.string),
};

export default UpcomingEventCard;
